import { useNotifications, useMarkNotificationRead } from "@/hooks/useNotifications";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Bell, CheckCircle, XCircle, Clock, Mail, TrendingUp, Calendar, Users, Check } from "lucide-react";
import { format } from "date-fns";

const typeConfig: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  hot_lead_alert: { label: "Hot Lead", icon: TrendingUp, color: "bg-orange-500" },
  deal_stage_change: { label: "Deal Stage", icon: TrendingUp, color: "bg-blue-500" },
  scheduled_call_reminder: { label: "Call Reminder", icon: Calendar, color: "bg-purple-500" },
  new_lead: { label: "New Lead", icon: Users, color: "bg-green-500" },
};

const statusConfig: Record<string, { label: string; icon: React.ElementType; variant: "default" | "destructive" | "secondary" }> = {
  sent: { label: "Sent", icon: CheckCircle, variant: "default" },
  failed: { label: "Failed", icon: XCircle, variant: "destructive" },
  pending: { label: "Pending", icon: Clock, variant: "secondary" },
};

export default function Notifications() {
  const { data: notifications, isLoading } = useNotifications();
  const markAsRead = useMarkNotificationRead();

  const unreadCount = notifications?.filter((n) => !n.is_read).length || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Notifications Log</h1>
          <p className="text-muted-foreground">View history of all sent alerts and their delivery status</p>
        </div>
        {unreadCount > 0 && (
          <Badge variant="secondary">{unreadCount} unread</Badge>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Email Notifications
          </CardTitle>
          <CardDescription>
            Recent email notifications sent from the system
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 p-4 border rounded-lg">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : notifications?.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No notifications yet</h3>
              <p className="text-muted-foreground">
                Email notifications will appear here when they are sent
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications?.map((notification) => {
                const typeInfo = typeConfig[notification.type] || { 
                  label: notification.type, 
                  icon: Bell, 
                  color: "bg-gray-500" 
                };
                const statusInfo = statusConfig[notification.status] || statusConfig.pending;
                const TypeIcon = typeInfo.icon;
                const StatusIcon = statusInfo.icon;

                return (
                  <div
                    key={notification.id}
                    className={`flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors ${
                      !notification.is_read ? "bg-primary/5 border-primary/20" : ""
                    }`}
                  >
                    <div className={`p-2 rounded-full ${typeInfo.color} text-white`}>
                      <TypeIcon className="h-4 w-4" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-medium ${!notification.is_read ? "text-foreground" : ""}`}>
                          {notification.subject}
                        </span>
                        <Badge variant={statusInfo.variant} className="gap-1">
                          <StatusIcon className="h-3 w-3" />
                          {statusInfo.label}
                        </Badge>
                        {!notification.is_read && (
                          <span className="h-2 w-2 rounded-full bg-primary" />
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {notification.recipient_email}
                        </span>
                        <span>•</span>
                        <Badge variant="outline" className="text-xs">
                          {typeInfo.label}
                        </Badge>
                      </div>
                      
                      {notification.error_message && (
                        <p className="mt-2 text-sm text-destructive">
                          Error: {notification.error_message}
                        </p>
                      )}
                      
                      <p className="mt-1 text-xs text-muted-foreground">
                        {format(new Date(notification.created_at), "MMM d, yyyy 'at' h:mm a")}
                      </p>
                    </div>

                    {!notification.is_read && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="shrink-0"
                        onClick={() => markAsRead.mutate(notification.id)}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Mark read
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
